import React, { useCallback, useMemo, useState } from 'react';
import {
  Badge, Button,
} from 'react-bootstrap';
import {
  AiOutlineDelete, AiOutlineMenu,
} from 'react-icons/ai';
import { useTranslation } from 'react-i18next';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { FiDownload } from 'react-icons/fi';
import { ImAttachment } from 'react-icons/im';
import { IoEyeOutline } from 'react-icons/io5';
import useTablePersistentSelection from 'hooks/useTablePersistentSelection';
import TextColumn from 'components/EditableTable/TextColumn';
import DateColumn from 'components/EditableTable/DateColumn';
import DropdownColumn from 'components/EditableTable/DropdownColumn';
import TableEdit from 'components/EditableTable/Table';
import DropdownActions from 'components/EditableTable/DropdownActions';
import CheckColumn from 'components/EditableTable/CheckColumn';
import Checkbox from 'components/Form/FormCheckbox';
import DocDetailOffcanvas from 'components/DocsManagment/DetailOffcanvas';
import useModal from 'hooks/useModal';
import DocStatusBadge from 'components/DocsManagment/StatusBadge';
import { DocNotePayload, DocRequestEntity, DocRequestStatus } from 'types/DocRequest';
import handlePromise from 'utils/Promise';
import DocsRequestApi from 'services/api/DocsRequest';
import classNames from 'classnames';
import UploadDocModal from 'components/DocsManagment/UploadDocModal';
import DocNoteModal from 'components/DocsManagment/NoteModal';
import SortIcon from 'components/DocsManagment/SortIcon';

type SortDirection = 'Ascending' | 'Descending' | '';
interface SortState {
  orderBy: string;
  sortDirection: SortDirection;
}

type DocsTableEditProps = {
  data?: DocRequestEntity[]
  reloadData: ( newData: DocRequestEntity ) => void
  handleViewDocument: (
    documentoId: number,
    cuentaDeInversionID: number,
    url: string,
    nombreArchivo: string,
    tipoArchivo: string,
    descargar: boolean,
  ) => void;
  currentSort: SortState;
  onSortChange: ( newOrderBy: string ) => void;
};

const SORTABLE_COLUMNS: Record<string, string> = {
  cuentaDeInversionName: 'cuentaDeInversionName',
  nombre: 'nombre',
  descripcion: 'descripcion',
  estado: 'estado',
  fechaLimite: 'fechaLimite',
  fechaExpiracion: 'fechaExpiracion',
};

function SortableHeader( {
  children,
  orderBy,
  currentSort,
  onSortChange,
  className,
  style,
}: {
  children: React.ReactNode,
  orderBy: string,
  currentSort: SortState,
  onSortChange: ( newOrderBy: string ) => void,
  className?: string,
  style?: React.CSSProperties,
} ) {
  const isSorted = currentSort.orderBy === orderBy;
  const isDescending = isSorted && currentSort.sortDirection === 'Descending';
  const isAscending = isSorted && currentSort.sortDirection === 'Ascending';

  return (
    <th
      onClick={() => onSortChange( orderBy )}
      style={{ cursor: 'pointer', ...style }}
      className={className}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: ( className && className.includes( 'text-center' ) ) ? 'center' : 'flex-start' }}>
        {children}
        <SortIcon isSorted={isSorted} isDescending={isDescending} isAscending={isAscending} />
      </div>
    </th>
  );
}

function DocsTableEdit( {
  data, reloadData, handleViewDocument,
  currentSort, onSortChange,
}: DocsTableEditProps ) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState( false );
  const [reloaded, setReloaded] = useState( new Date().valueOf() );
  const {
    // toggleAllRows,
    toggleRow,
    isRowSelected,
  } = useTablePersistentSelection<any>( 'id' );
  const {
    showModal: showCanvas,
    closeModal: closeCanvas,
    openModal: openCanvas,
    selectedItem,
  } = useModal<DocRequestEntity>();

  const {
    showModal: showNoteModal,
    closeModal: closeNoteModal,
    openModal: openNoteModal,
    selectedItem: selectedItemUpdate,
  } = useModal<DocNotePayload>();

  const {
    showModal: showUploadFileModal,
    closeModal: closeUploadFileModal,
    openModal: openUploadFileModal,
    selectedItem: itemUploadFile,
  } = useModal<DocRequestEntity>();

  const submitForm = useCallback( async (
    docId: number,
    field: string,
    value?: string | boolean,
    note?: string,
  ) => {
    if ( ( typeof value === 'string' && value ) || typeof value === 'boolean' ) {
      setLoading( true );
      const dataToSend = {
        nota: note,
        solicitudData: {
          [field]: value,
        },
      };

      const [, response, responseData] = await handlePromise(
        DocsRequestApi.editDocRequests( docId, dataToSend ),
      );

      if ( response.ok ) {
        reloadData( responseData );
      }
      setLoading( false );
    }
  }, [] );

  const statuses = useMemo( () => Object.values( DocRequestStatus ).map( ( status ) => ( {
    id: status,
    label: <DocStatusBadge status={status} />,
    icon: <DocStatusBadge status={status} showLabel={false} />,
  } ) ), [] );

  return (
    <>
      <TableEdit>
        <thead>
          <tr>
            <th>
              <DropdownActions
                actions={[
                  {
                    label: t( 'admin.docsDashboard.table.selectAll' ),
                    onClick: () => { },
                  },
                  {
                    label: t( 'admin.docsDashboard.table.edit' ),
                    onClick: () => {},
                  },
                  {
                    label: t( 'admin.docsDashboard.table.download' ),
                    onClick: () => {},
                  },
                  {
                    label: t( 'admin.docsDashboard.table.delete' ),
                    onClick: () => {},
                  },
                ]}
              />
            </th>
            <th>{t( 'admin.docsDashboard.table.id' )}</th>
            <SortableHeader
              orderBy={SORTABLE_COLUMNS.cuentaDeInversionName}
              currentSort={currentSort}
              onSortChange={onSortChange}
              style={{ minWidth: 190 }}
            >
              {t( 'admin.docsDashboard.table.investmentAccount' )}
            </SortableHeader>
            <SortableHeader
              orderBy={SORTABLE_COLUMNS.nombre}
              currentSort={currentSort}
              onSortChange={onSortChange}
            >
              {t( 'admin.docsDashboard.table.name' )}
            </SortableHeader>
            <SortableHeader
              orderBy={SORTABLE_COLUMNS.descripcion}
              currentSort={currentSort}
              onSortChange={onSortChange}
              className="text-truncate-2"
            >
              {t( 'admin.docsDashboard.table.description' )}
            </SortableHeader>
            <SortableHeader
              orderBy={SORTABLE_COLUMNS.estado}
              currentSort={currentSort}
              onSortChange={onSortChange}
              className="text-center"
              style={{ width: 110 }}
            >
              {t( 'admin.docsDashboard.table.status' )}
            </SortableHeader>
            <SortableHeader
              orderBy={SORTABLE_COLUMNS.fechaLimite}
              currentSort={currentSort}
              onSortChange={onSortChange}
              className="text-center"
              style={{ width: 120 }}
            >
              {t( 'admin.docsDashboard.table.limitDateShort' )}
            </SortableHeader>
            <SortableHeader
              orderBy={SORTABLE_COLUMNS.fechaExpiracion}
              currentSort={currentSort}
              onSortChange={onSortChange}
              className="text-center"
              style={{ width: 130 }}
            >
              {t( 'admin.docsDashboard.table.expirationDateShort' )}
            </SortableHeader>

            <th style={{ width: 160 }} />
            <th style={{ width: 60 }} />
          </tr>
        </thead>
        {/* ... (resto del tbody y modales) */}
        <tbody className={classNames( {
          'app-component-loading': loading,
        } )}
        >
          {data?.map( ( item ) => (
            <tr key={item.id}>
              <td>
                <CheckColumn
                  checked={isRowSelected( String( item.id ) )}
                  onChange={() => toggleRow( item )}
                />
              </td>
              <td>{item.id}</td>
              <td>
                {item.cuentaDeInversionName}
              </td>
              <td>
                <TextColumn
                  value={item.nombre}
                  onChange={( value ) => submitForm( item.id, 'nombre', value )}
                />
              </td>
              <td>
                <div className="text-truncate-2">
                  <TextColumn
                    value={item.descripcion}
                    onChange={( value ) => submitForm( item.id, 'descripcion', value )}
                  />
                </div>
              </td>
              <td>
                <DropdownColumn
                  key={reloaded}
                  value={item.estado}
                  onChange={( value ) => openNoteModal( {
                    ...item,
                    newValue: value as DocRequestStatus,
                    field: 'estado',
                  } )}
                  options={statuses}
                  placeholder={(
                    <Badge bg="light">
                      <IoMdInformationCircleOutline size={11} />
                    </Badge>
                )}
                />
              </td>
              <td className="text-center">
                <DateColumn
                  value={item.fechaLimite}
                  onChange={( value ) => submitForm( item.id, 'fechaLimite', value )}
                  className="text--black10"
                />
              </td>
              <td className="text-center">
                <DateColumn
                  key={reloaded}
                  value={item.fechaExpiracion}
                  onChange={( value ) => openNoteModal( {
                    ...item,
                    newValue: value,
                    field: 'fechaExpiracion',
                  } )}
                  className="text--black10"
                />
              </td>
              <td className="text-center">
                <Button
                  className="px-1 py-0"
                  variant="link"
                  onClick={() => openCanvas( item )}
                >
                  <AiOutlineMenu />
                </Button>
                <Button
                  className="px-1 py-0"
                  variant="link"
                  disabled={!( !!item.nombreArchivo && !!item.urlArchivo )}
                  onClick={() => {
                    if ( !item.id || !item.urlArchivo || !item.nombreArchivo
                      || !item.tipoArchivo
                    ) return;
                    handleViewDocument(
                      item.id,
                      item.cuentaDeInversionID,
                      item.urlArchivo,
                      item.nombreArchivo,
                      item.tipoArchivo,
                      false,
                    );
                  }}
                >
                  <IoEyeOutline />
                </Button>
                <Button
                  className="px-1 py-0"
                  variant="link"
                  onClick={() => openUploadFileModal( item )}
                >
                  <ImAttachment className="text--md" />
                </Button>
                <Button
                  className="px-1 py-0"
                  variant="link"
                  disabled={!( !!item.nombreArchivo && !!item.urlArchivo )}
                  onClick={() => {
                    if ( !item.id || !item.urlArchivo || !item.nombreArchivo
                      || !item.tipoArchivo
                    ) return;
                    handleViewDocument(
                      item.id,
                      item.cuentaDeInversionID,
                      item.urlArchivo,
                      item.nombreArchivo,
                      item.tipoArchivo,
                      true,
                    );
                  }}
                >
                  <FiDownload />
                </Button>
                <Button
                  className="px-1 py-0"
                  variant="link"
                >
                  <AiOutlineDelete />
                </Button>
              </td>
              <td className="text-end pe-0">
                <Checkbox
                  showErrors={false}
                  value={item.activo}
                  type="switch"
                  onChange={( value ) => submitForm( item.id, 'activo', value )}
                  classNames={{
                    formGroup: 'mb-0 mt-1',
                  }}
                />
              </td>
            </tr>
          ) )}
        </tbody>
      </TableEdit>

      {showCanvas && (
      <DocDetailOffcanvas
        onClose={closeCanvas}
        data={selectedItem}
        handleViewDocument={handleViewDocument}
      />
      )}
      {showUploadFileModal && (
      <UploadDocModal
        data={itemUploadFile}
        onClose={closeUploadFileModal}
        reloadData={reloadData}
      />
      )}
      {showNoteModal && (
      <DocNoteModal
        onSubmit={( { nota } ) => {
          if ( selectedItemUpdate?.id ) {
            closeNoteModal();
            submitForm(
              selectedItemUpdate.id,
              selectedItemUpdate.field,
              selectedItemUpdate.newValue,
              nota,
            );
          }
        }}
        onDismissChange={() => {
          setReloaded( new Date().valueOf() );
          closeNoteModal();
        }}
      />
      )}
    </>
  );
}

export default DocsTableEdit;
